import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInquiry } from '../../context/InquiryContext';
import { useAuth } from '../../context/AuthContext';
import { shouldShowVerifiedBadge } from '../../utils/membershipUtils';
import { useCurrency } from '../../context/CurrencyContext';
import LoginModal from '../LoginModal/LoginModal';
import './ProductCardFigma.css';
import { PRODUCT_PLACEHOLDER, LOGO_PLACEHOLDER } from '../../utils/placeholders';

/**
 * ProductCardFigma - Reusable Product Card Component (Figma Design)
 * 
 * @param {Object} product - Product data object
 * @param {number} product.id - Product ID
 * @param {string} product.name - Product name
 * @param {string} product.description - Product description
 * @param {string} product.image - Product image URL
 * @param {number} product.priceMin - Minimum price
 * @param {number} product.priceMax - Maximum price
 * @param {string} product.unit - Price unit (e.g., "Piece", "Sqft", "Length")
 * @param {string} product.moq - Minimum order quantity
 * @param {Object} product.seller - Seller information
 * @param {string} product.seller.name - Seller/Company name
 * @param {string} product.seller.logo - Seller logo URL
 * @param {string} product.seller.country - Seller country
 * @param {string} product.seller.countryFlag - Country flag URL
 * @param {number} product.seller.memberYears - Years as member
 * @param {string} product.seller.businessType - Business type (e.g., "Manufacturer")
 * @param {string} product.seller.membershipPlan - Membership plan (Basic, GCC Business, Global)
 * @param {boolean} product.seller.isVerified - Whether seller is verified
 * 
 * @example
 * // Basic usage
 * <ProductCardFigma product={productData} />
 * 
 * // With custom click handler
 * <ProductCardFigma product={productData} onCardClick={(product) => console.log(product)} />
 * 
 * // Hide inquiry button
 * <ProductCardFigma product={productData} showInquiry={false} />
 */
const ProductCardFigma = ({ 
  product, 
  onCardClick, 
  showInquiry = true,
  showViewDetails = true,
  className = '' 
}) => {
  const navigate = useNavigate();
  const { addToInquiry, isInInquiry, removeFromInquiry } = useInquiry();
  const { user, isAuthenticated } = useAuth();
  const { formatPriceRange, selectedCurrency } = useCurrency();
  const [showAdded, setShowAdded] = useState(false);
  const [showOwnProductError, setShowOwnProductError] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // Return null if no product data
  if (!product) return null;

  // Extract seller data
  const seller = product.seller || {};
  
  // Normalize product data
  const productData = {
    id: product.id,
    name: product.name || product.description || 'Untitled Product',
    description: product.description || product.shortDescription || product.name || '',
    image: product.image || PRODUCT_PLACEHOLDER,
    priceMin: product.priceMin || 0,
    priceMax: product.priceMax || 0,
    unit: product.unit || product.pricePer || 'Unit',
    moq: product.moq || '1 Unit',
    // Seller info
    companyName: seller.name || product.companyName || 'Unknown Seller',
    companyLogo: seller.logo || product.sellerAvatar || LOGO_PLACEHOLDER,
    country: seller.country || product.country || 'UAE',
    countryFlag: seller.countryFlag || product.countryFlag || 'https://flagcdn.com/w80/ae.png',
    memberYears: seller.memberYears || product.memberYears || 1,
    businessType: seller.businessType || product.businessType || 'Manufacturer',
    membershipPlan: seller.membershipPlan || product.membershipPlan || 'Basic',
    isVerified: seller.isVerified || product.isVerified || false,
    sellerId: seller.userId || seller.id || product.sellerId || product.userId,
    phone: seller.phone || seller.phoneNumber || product.phone || product.phoneNumber || '',
  };

  const isAdded = isInInquiry(productData.id);
  
  // Check if this is user's own product
  const isOwnProduct = user && user.sellerId && (
    user.sellerId === productData.sellerId || 
    user.sellerId === product.seller?.id
  );

  // Get membership plan config - handles all possible plan names from database
  const getPlanConfig = (plan) => {
    const planLower = (plan || 'Basic').toLowerCase();
    
    // Silver/Basic icon
    const basicIcon = '/uploads/builder-images/d8ee44da49d1e0c28cf5bfe995ca6aa7f62420ea_w44.png';
    // Gold/GCC icon
    const gccIcon = '/uploads/builder-images/cdcdf2f44d98a4cc6cdc74b524bc4359f623ed6c_w44.png';
    // Platinum/Global icon
    const globalIcon = '/uploads/builder-images/050559d54da8d35ba6d11841efdb737d4c3ae660_w44.png';
    
    // Check for GCC/Premium plans
    if (planLower.includes('gcc') || planLower.includes('premium') || planLower.includes('gold')) {
      return { color: '#009D0A', icon: gccIcon, label: 'GCC Business' };
    }
    
    // Check for Global/Enterprise plans
    if (planLower.includes('global') || planLower.includes('enterprise') || planLower.includes('platinum')) {
      return { color: '#FF6B00', icon: globalIcon, label: 'Global' };
    }
    
    // Default to Basic
    return { color: '#6D6D6D', icon: basicIcon, label: 'Basic' };
  };

  const planConfig = getPlanConfig(productData.membershipPlan);

  // Handle card click
  const handleCardClick = (e) => {
    if (e.target.closest('button')) return;
    if (onCardClick) {
      onCardClick(product);
    } else {
      navigate(`/product/${productData.id}`);
    }
  };

  // Handle inquire button click
  const handleInquireClick = (e) => {
    e.stopPropagation();
    
    // Check if user is logged in first
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    
    if (isOwnProduct) {
      setShowOwnProductError(true);
      setTimeout(() => setShowOwnProductError(false), 3000);
      return;
    }
    
    if (isAdded) {
      removeFromInquiry(productData.id);
    } else {
      addToInquiry({
        id: productData.id,
        image: productData.image,
        description: productData.description,
        priceMin: productData.priceMin,
        priceMax: productData.priceMax,
        priceUnit: selectedCurrency.code,
        pricePer: productData.unit,
        moq: productData.moq,
        companyName: productData.companyName,
        sellerId: productData.sellerId,
        sellerName: productData.companyName,
        sellerAvatar: productData.companyLogo,
        country: productData.country,
        countryFlag: productData.countryFlag
      });
      setShowAdded(true);
      setTimeout(() => setShowAdded(false), 2000);
    }
  };

  // Handle call button click - opens phone dialer
  const handleCallClick = (e) => {
    e.stopPropagation();
    if (productData.phone) {
      window.location.href = `tel:${productData.phone}`;
    } else {
      alert('Phone number not available for this seller');
    }
  };

  return (
    <div className={`pcf-card ${className}`} onClick={handleCardClick}>
      {/* Product Image */}
      <div className="pcf-image-section">
        <img 
          src={productData.image} 
          alt={productData.name} 
          className="pcf-image"
        />
      </div>
      
      {/* Product Details */}
      <div className="pcf-details-section">
        {/* Description Block */}
        <div className="pcf-info-block">
          <div className="pcf-desc-block">
            <p className="pcf-product-name">{productData.name}</p>
            
            {/* Price Block */}
            <div className="pcf-price-block">
              <div className="pcf-price-row">
                <span className="pcf-price-amount">{formatPriceRange(productData.priceMin, productData.priceMax)}</span>
                <span className="pcf-price-unit">/ {productData.unit}</span>
              </div>
              <p className="pcf-moq">
                <span className="pcf-moq-label">(MOQ) </span>
                <span className="pcf-moq-value">{productData.moq}</span>
              </p>
            </div>
          </div>
          
          {/* Badges Block */}
          <div className="pcf-badges-block">
            {/* Membership Plan + Verified Row */}
            <div className="pcf-badges-row">
              <div className="pcf-plan-badge">
                {planConfig.icon && (
                  <img src={planConfig.icon} alt={planConfig.label} className="pcf-plan-icon" />
                )}
                <span className="pcf-plan-text" style={{ color: planConfig.color }}>{planConfig.label}</span>
              </div>
              
              {shouldShowVerifiedBadge(productData.membershipPlan) && (
                <div className="pcf-verified-badge">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 0C11.9323 0 12.6871 1.26712 13.5472 1.49659C14.4377 1.73472 15.7221 1.02177 16.5015 1.47205C17.2923 1.92954 17.314 3.40305 17.9562 4.04382C18.5984 4.68605 20.0705 4.70769 20.528 5.49856C20.9782 6.27788 20.2653 7.56231 20.5034 8.45276C20.7329 9.31291 22 10.0663 22 11C22 11.9337 20.7329 12.6871 20.5034 13.5472C20.2653 14.4377 20.9782 15.7221 20.528 16.5015C20.0705 17.2923 18.597 17.314 17.9562 17.9562C17.314 18.5984 17.2923 20.0705 16.5015 20.528C15.7221 20.9782 14.4377 20.2653 13.5472 20.5034C12.6871 20.7329 11.9337 22 11 22C10.0663 22 9.31291 20.7329 8.45276 20.5034C7.56231 20.2653 6.27788 20.9782 5.49856 20.528C4.70769 20.0705 4.68605 18.597 4.04382 17.9562C3.4016 17.3154 1.92954 17.2923 1.47205 16.5015C1.02177 15.7221 1.73472 14.4377 1.49659 13.5472C1.26712 12.6871 0 11.9338 0 11C0 10.0663 1.26712 9.31291 1.49659 8.45276C1.73472 7.56231 1.02177 6.27788 1.47205 5.49856C1.92954 4.70769 3.40305 4.68605 4.04382 4.04382C4.68605 3.4016 4.70769 1.92954 5.49856 1.47205C6.27788 1.02177 7.56231 1.73472 8.45276 1.49659C9.31291 1.26712 10.0663 0 11 0Z" fill="#29ABE2"/>
                    <path d="M15.5876 7.60149C15.1229 7.13679 14.3696 7.13679 13.9049 7.60149L9.03125 12.4751L7.00786 10.4518C6.54315 9.98706 5.7898 9.98706 5.32509 10.4518C4.86039 10.9165 4.86039 11.6698 5.32509 12.1345L8.14798 14.9574C8.39188 15.2013 8.71372 15.3168 9.03266 15.3052C9.35161 15.3168 9.67345 15.2013 9.91735 14.9574L15.5891 9.28568C16.0538 8.82097 16.0538 8.0676 15.5891 7.60289L15.5876 7.60149Z" fill="white"/>
                  </svg>
                  <span>Verified</span>
                </div>
              )}
            </div>
            
            {/* Company Name */}
            <div className="pcf-company-row">
              <img src={productData.companyLogo} alt={productData.companyName} className="pcf-company-logo" />
              <span className="pcf-company-name">
                <span className="desktop-text">{productData.companyName}</span>
                <span className="mobile-text">{productData.companyName?.split(' ').length > 2 ? productData.companyName.split(' ').slice(0, 2).join(' ') + '...' : productData.companyName}</span>
              </span>
            </div>
            
            {/* Country + Years */}
            <div className="pcf-location-row">
              <div className="pcf-flag-container">
                <img src={productData.countryFlag} alt="Flag" className="pcf-flag-img" />
              </div>
              <span className="pcf-location-text">
                <span className="pcf-country">{productData.country} / </span>
                <span className="pcf-years">{productData.memberYears} Year{productData.memberYears > 1 ? 's' : ''}</span>
              </span>
            </div>
            
            {/* Business Type */}
            <div className="pcf-business-type">
              <span>{productData.businessType}</span>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        {(showInquiry || showViewDetails) && (
          <div className="pcf-actions-row">
            {showOwnProductError && (
              <div className="pcf-error-message">Cannot inquire on your own product</div>
            )}
            
            {showInquiry && (
              <button 
                className={`pcf-btn-inquire ${isAdded ? 'added' : ''} ${isOwnProduct ? 'disabled' : ''}`}
                onClick={handleInquireClick}
              >
                {showAdded ? '✓ Added!' : isAdded ? 'Remove' : <><span className="desktop-text">Add to Quotation</span><span className="mobile-text">+ to quote</span></>}
              </button>
            )}
            
            {showViewDetails && (
              <button className="pcf-btn-call" onClick={handleCallClick}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 16.92V19.92C22 20.4704 21.7893 20.9981 21.4142 21.3981C21.0391 21.7982 20.5304 22.0362 19.98 22.0699C19.643 22.0899 19.2989 22.0999 18.95 22.0999C10.21 22.0999 3.10001 14.9899 3.10001 6.24994C3.10001 5.90094 3.11001 5.55694 3.13001 5.21994C3.16376 4.66947 3.40177 4.16077 3.80183 3.78568C4.20189 3.41059 4.72954 3.19994 5.28001 3.19994H8.28001C8.72424 3.19557 9.15598 3.35553 9.49332 3.64969C9.83067 3.94386 10.0507 4.35171 10.11 4.79194C10.2199 5.66094 10.4293 6.51394 10.73 7.32994C10.8567 7.66661 10.8837 8.03161 10.808 8.38227C10.7323 8.73294 10.5571 9.05427 10.3 9.30994L9.00001 10.6099C10.3612 13.1755 12.4245 15.2387 14.99 16.5999L16.29 15.2999C16.5457 15.0429 16.8671 14.8677 17.2177 14.792C17.5684 14.7163 17.9334 14.7433 18.27 14.8699C19.086 15.1707 19.9391 15.3801 20.808 15.4899C21.2532 15.5498 21.6651 15.7746 21.9605 16.1183C22.256 16.462 22.4131 16.9009 22.4 17.3499L22 16.92Z" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Call</span>
              </button>
            )}
          </div>
        )}
      </div>
      
      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)}
        onRegisterClick={() => {
          setShowLoginModal(false);
          navigate('/register');
        }}
      />
    </div>
  );
};

export default ProductCardFigma;
